import { useEffect, useMemo, useState } from 'react'
import { container } from 'tsyringe'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ApiError, ApiService } from '../api/ApiService'
import type { UserSummary } from '../shared/types/ChatSummary'
import type { ModelResponse } from '../shared/types/Model'
import type { Mode } from '../shared/types/Mode'
import './ChatHistoryModal.css'

interface ChatHistoryModalProps {
  model: ModelResponse
  mode: Mode
  botUsername?: string
  onClose: () => void
  onOpenTelegramLink: (url: string) => void
}

export function ChatHistoryModal({
  model,
  mode,
  botUsername,
  onClose,
  onOpenTelegramLink,
}: ChatHistoryModalProps) {
  const { t } = useTranslation()
  const api = useMemo(() => container.resolve(ApiService), [])
  const [chats, setChats] = useState<UserSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyChatId, setBusyChatId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const isRoleplay = mode === 'roleplay'

  const loadChats = async () => {
    setLoading(true)
    setError(null)
    try {
      const currentUser = await api.getCurrentUser()
      setChats(
        currentUser.chatSummaries
          .filter((chat) => chat.modelId === model.id && chat.isRoleplay === isRoleplay)
          .sort((left, right) => compareDate(right.updatedAt, left.updatedAt)),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chat_history_load_failed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadChats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, mode])

  const openChat = async (chatId: number) => {
    if (!botUsername) return
    setBusyChatId(chatId)
    setError(null)
    try {
      await api.setTelegramActiveChat({ chatId, modelId: model.id, isRoleplay })
      onOpenTelegramLink(buildTelegramChatUrl(botUsername, chatId))
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chat_history_open_failed'))
    } finally {
      setBusyChatId(null)
    }
  }

  const createNewChat = async () => {
    if (!botUsername) return
    setCreating(true)
    setError(null)
    try {
      const chat = await api.createChat(model.id, isRoleplay)
      await api.setTelegramActiveChat({ chatId: chat.id, modelId: model.id, isRoleplay })
      onOpenTelegramLink(buildTelegramChatUrl(botUsername, chat.id))
      onClose()
    } catch (err) {
      setError(formatCreateChatError(err, t))
    } finally {
      setCreating(false)
    }
  }

  const deleteChat = async (chatId: number) => {
    const confirmed = window.confirm(t('chat_history_delete_confirm'))
    if (!confirmed) return

    setBusyChatId(chatId)
    setError(null)
    try {
      await api.deleteChat(chatId)
      setChats((current) => current.filter((chat) => chat.chatId !== chatId))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chat_history_delete_failed'))
    } finally {
      setBusyChatId(null)
    }
  }

  return (
    <div className="chat-history-backdrop" onClick={onClose}>
      <section className="chat-history-modal" onClick={(event) => event.stopPropagation()}>
        <header className="chat-history-header">
          <div>
            <p className="chat-history-eyebrow">{isRoleplay ? t('tab_roleplay') : t('tab_realistic')}</p>
            <h2>{model.name}</h2>
          </div>
          <button type="button" className="chat-history-close" onClick={onClose} aria-label={t('close')}>
            ×
          </button>
        </header>

        {error ? <p className="chat-history-error">{error}</p> : null}

        <div className="chat-history-list">
          {loading ? (
            <p className="chat-history-empty">{t('chat_history_loading')}</p>
          ) : chats.length ? (
            chats.map((chat) => (
              <article key={chat.chatId} className="chat-history-row">
                <div className="chat-history-row-main">
                  <h3>{chat.chatName}</h3>
                  <p className="chat-history-date">{formatDate(chat.updatedAt)}</p>
                  <p className="chat-history-preview">{formatPreview(chat.lastMessage, t('chat_history_photo'))}</p>
                </div>
                <div className="chat-history-actions">
                  <button
                    type="button"
                    className="chat-history-primary"
                    onClick={() => void openChat(chat.chatId)}
                    disabled={!botUsername || busyChatId === chat.chatId || creating}
                  >
                    {busyChatId === chat.chatId ? t('loading') : t('chat_history_continue')}
                  </button>
                  <button
                    type="button"
                    className="chat-history-delete"
                    onClick={() => void deleteChat(chat.chatId)}
                    disabled={busyChatId === chat.chatId || creating}
                  >
                    {t('chat_history_delete')}
                  </button>
                </div>
              </article>
            ))
          ) : (
            <p className="chat-history-empty">{t('chat_history_empty')}</p>
          )}
        </div>

        <button
          type="button"
          className="chat-history-new"
          onClick={() => void createNewChat()}
          disabled={!botUsername || creating || busyChatId !== null}
        >
          {creating ? t('loading') : t('chat_history_new_chat')}
        </button>
      </section>
    </div>
  )
}

function buildTelegramChatUrl(botUsername: string, chatId: number) {
  return `https://t.me/${botUsername}?start=chat_${chatId}`
}

function compareDate(left?: string, right?: string) {
  return parseDate(left) - parseDate(right)
}

function parseDate(value?: string) {
  if (!value) return 0
  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

function formatDate(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatPreview(value: string | undefined, photoLabel: string) {
  const trimmed = value?.trim()
  if (!trimmed) return ''
  return trimmed.toLowerCase().startsWith('image:') ? photoLabel : trimmed
}

function formatCreateChatError(error: unknown, t: TFunction) {
  if (error instanceof ApiError) {
    if (error.type === 'ChatCreationCooldown') {
      return t('chat_history_create_cooldown', { seconds: error.retryAfterSeconds ?? 30 })
    }

    if (error.type === 'ChatLimitReached') {
      return t('chat_history_create_limit', { max: error.maxChats ?? 50 })
    }
  }

  return error instanceof Error ? error.message : t('chat_history_create_failed')
}
